/**
 * Formato de textos y numeros de la interfaz (todo en espanol).
 *
 * Se centraliza aqui para que todas las pantallas muestren horas, fechas,
 * duraciones y contadores con el mismo criterio.
 */

const dateTimeFormatter = new Intl.DateTimeFormat('es-ES', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const timeFormatter = new Intl.DateTimeFormat('es-ES', {
  hour: '2-digit',
  minute: '2-digit',
});

const dayFormatter = new Intl.DateTimeFormat('es-ES', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const dayShortFormatter = new Intl.DateTimeFormat('es-ES', {
  weekday: 'long',
  day: '2-digit',
  month: 'long',
});

const numberFormatter = new Intl.NumberFormat('es-ES');

export const EMPTY_VALUE = '—';

export function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Hora corta para la lista de conversaciones y las burbujas. */
export function formatTime(value: string | null | undefined): string {
  const date = parseDate(value);
  return date ? timeFormatter.format(date) : '';
}

export function formatDateTime(value: string | null | undefined): string {
  const date = parseDate(value);
  return date ? dateTimeFormatter.format(date) : EMPTY_VALUE;
}

/** Separador de dia del hilo: "Hoy", "Ayer" o la fecha. */
export function formatDayLabel(value: string | Date): string {
  const date = value instanceof Date ? value : parseDate(value);
  if (!date) return EMPTY_VALUE;
  const today = startOfDay(new Date());
  const target = startOfDay(date);
  const diffDays = Math.round((today.getTime() - target.getTime()) / 86_400_000);
  if (diffDays === 0) return 'Hoy';
  if (diffDays === 1) return 'Ayer';
  if (diffDays > 1 && diffDays < 7) return dayShortFormatter.format(date);
  return dayFormatter.format(date);
}

/** "ahora", "hace 12 min", "hace 3 h", "hace 2 d" o la fecha completa. */
export function formatRelative(value: string | null | undefined): string {
  const date = parseDate(value);
  if (!date) return '';
  const diffMs = Date.now() - date.getTime();
  const seconds = Math.round(diffMs / 1000);
  if (seconds < 60) return 'ahora';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.round(hours / 24);
  if (days < 7) return `hace ${days} d`;
  return dayFormatter.format(date);
}

/** Duracion legible a partir de segundos (tiempo medio de primera respuesta). */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return EMPTY_VALUE;
  if (seconds < 60) return `${Math.round(seconds)} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ${Math.round(seconds % 60)} s`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ${minutes % 60} min`;
  const days = Math.floor(hours / 24);
  return `${days} d ${hours % 24} h`;
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EMPTY_VALUE;
  return numberFormatter.format(value);
}

/** `MetricsResponse.automationRate` es un porcentaje (0-100). */
export function formatPercent(value: number | null | undefined, decimals = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EMPTY_VALUE;
  const rounded = Number(value.toFixed(decimals));
  return `${numberFormatter.format(rounded)} %`;
}

export function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
}

/** Iniciales para el avatar: "Maria Lopez" -> "ML". */
export function initials(name: string | null | undefined, fallback = '?'): string {
  const clean = (name ?? '').trim();
  if (!clean) return fallback;
  const words = clean.split(/\s+/).filter((word) => word.length > 0);
  if (words.length === 0) return fallback;
  const first = words[0] ?? '';
  if (words.length === 1) return first.slice(0, 2).toUpperCase();
  const last = words[words.length - 1] ?? '';
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

/** Numero de telefono de WhatsApp con el prefijo `+` si viene sin el. */
export function formatPhone(waId: string | null | undefined): string {
  const clean = (waId ?? '').trim();
  if (!clean) return EMPTY_VALUE;
  return clean.startsWith('+') ? clean : `+${clean}`;
}

/** Valor de `ContactView.attributes` (JSON libre) para mostrarlo en la ficha. */
export function formatAttributeValue(value: unknown): string {
  if (value === null || value === undefined) return EMPTY_VALUE;
  if (typeof value === 'string') return value.length > 0 ? value : EMPTY_VALUE;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map((item) => formatAttributeValue(item)).join(', ');
  try {
    return JSON.stringify(value);
  } catch {
    return EMPTY_VALUE;
  }
}

/** Tiempo restante de la ventana de 24 h a partir de milisegundos. */
export function formatRemainingMs(remainingMs: number): string {
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) return 'cerrada';
  const totalMinutes = Math.floor(remainingMs / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes} min`;
  return `${hours} h ${minutes} min`;
}
